import { Auth } from "aws-amplify";
import { useCallback, useState } from "react";
import { useQueryCache } from "react-query";
import AppStateHandler from "src/StateHandlers/AppStateHandler";
import SnackbarHandler from "src/Utils/SnackbarHandler";

function useAuth() {
  const [loading, setLoading] = useState(false);
  const [isVerificationVisible, setIsVerificationVisible] = useState(false);
  const [forgetPasswordVisible, setForgetPasswordVisible] = useState(false);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const queryCache = useQueryCache();

  const login = useCallback(
    ({ email, password }: any) => {
      setLoading(true);
      Auth.signIn({
        username: email,
        password: password,
      })
        .then((res) => {
          setLoading(false);
          console.log("sign-in successfuly", res);
          queryCache.clear();
          AppStateHandler.loginUser(res);
        })
        .catch((err) => {
          setLoading(false);
          console.log("error in signing in", err);
          SnackbarHandler.authErrorToast(err?.message);
          if (err.code === "UserNotConfirmedException") {
            Auth.resendSignUp(email);
            setIsVerificationVisible(true);
          }
        });
    },
    [queryCache]
  );

  const verify = useCallback(
    (email: string, verificationCode: string, cb: any) => {
      setVerificationLoading(true);
      Auth.confirmSignUp(email, verificationCode)
        .then((res) => {
          console.log("verification successful", res);
          SnackbarHandler.successToast("User verified successfully");
          setIsVerificationVisible(false);
          setVerificationLoading(false);
          cb();
        })
        .catch((err) => {
          console.log("not verified", err);
          SnackbarHandler.authErrorToast(err.message);
          setVerificationLoading(false);
        });
    },
    []
  );

  const forgetPassword = useCallback(async ({ email }: any) => {
    try {
      setLoading(true);
      const response = await Auth.forgotPassword(email);
      SnackbarHandler.successToast(
        "Verfication code is sent to your email for resetting password."
      );
      setLoading(false);
      console.log(response);
      setChangePasswordVisible(true);
    } catch (error) {
      setLoading(false);
      console.log(error);
      if (error.code === "LimitExceededException") {
        SnackbarHandler.authErrorToast(error.message);
      }
    }
  }, []);

  const changePassword = useCallback(
    async ({ password, verificationCode }: any, email: string) => {
      try {
        setLoading(true);
        const response = await Auth.forgotPasswordSubmit(
          email,
          verificationCode,
          password
        );
        setLoading(false);
        console.log(response);
        SnackbarHandler.successToast(
          "Password changed successfully. Please login."
        );
        setForgetPasswordVisible(false);
      } catch (error) {
        setLoading(false);
        console.log(error);
      }
    },
    []
  );

  const signup = useCallback(
    ({ email, password, name, lastName, phone, state_id, city_id }: any) => {
      setLoading(true);
      Auth.signUp({
        username: email,
        password: password,
        attributes: {
          name: name,
          family_name: lastName,
          email: email,
          phone_number: `+213${phone}`,
          "custom:state_id": `${state_id}`,
          "custom:city_id": `${city_id}`,
        },
      })
        .then((res) => {
          setLoading(false);
          console.log("Signed up---", res);
          setIsVerificationVisible(true);
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
          SnackbarHandler.authErrorToast(err.message);
        });
    },
    []
  );

  return {
    login,
    signup,
    verify,
    loading,
    isVerificationVisible,
    setIsVerificationVisible,
    verificationLoading,
    forgetPasswordVisible,
    setForgetPasswordVisible,
    forgetPassword,
    changePasswordVisible,
    setChangePasswordVisible,
    changePassword,
  };
}

export default useAuth;
